/**
 * Session cache for account-hub GETs that are not /profiles/me/.
 * Fresh for the tab session until a write or logout invalidates it.
 */

export const ACCOUNT_HISTORY_KEY = 'user:account-history';
export const ACCOUNT_HISTORY_PATH = '/account/history/';
export const HUNT_LOCATIONS_KEY = 'user:hunt-locations';
export const HUNT_LOCATIONS_PATH = '/hunt-locations/';
export const NOTIFICATIONS_KEY_PREFIX = 'user:notifications:';

const memory = {
  history: null,
  locations: null,
  notifications: new Map(),
};

let inFlightHistory = null;
let inFlightLocations = null;
const inFlightNotifications = new Map();

export function notificationsListKey(filter = 'all') {
  return `${NOTIFICATIONS_KEY_PREFIX}${filter || 'all'}`;
}

export function isValidHistory(data) {
  return Array.isArray(data);
}

export function isValidLocations(data) {
  return Array.isArray(data);
}

export function isValidNotificationsPayload(data) {
  return (
    Boolean(data) &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Array.isArray(data.results) &&
    Number.isFinite(Number(data.count))
  );
}

function normalizeLocations(data) {
  if (isValidLocations(data)) return data;
  if (data && isValidLocations(data.results)) return data.results;
  return null;
}

function normalizeHistory(data) {
  if (isValidHistory(data)) return data;
  if (data && isValidHistory(data.results)) return data.results;
  return null;
}

function normalizeNotifications(data) {
  if (!data || typeof data !== 'object') return null;
  const results = Array.isArray(data.results) ? data.results : [];
  const count = Number(data.count);
  return { results, count: Number.isFinite(count) ? count : results.length };
}

function readStorageEntry(key, isValid) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isValid(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorageEntry(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // quota / private mode
  }
}

function writeHistory(list) {
  memory.history = { data: list, ts: Date.now() };
  writeStorageEntry(ACCOUNT_HISTORY_KEY, list);
}

function writeLocations(list) {
  memory.locations = { data: list, ts: Date.now() };
  writeStorageEntry(HUNT_LOCATIONS_KEY, list);
}

function writeNotifications(filter, payload) {
  const key = notificationsListKey(filter);
  memory.notifications.set(key, { data: payload, ts: Date.now() });
  writeStorageEntry(key, payload);
}

export function getCachedAccountHistory() {
  if (memory.history && isValidHistory(memory.history.data)) return memory.history.data;
  const stored = readStorageEntry(ACCOUNT_HISTORY_KEY, isValidHistory);
  if (!stored) return null;
  memory.history = stored;
  return stored.data;
}

export function setCachedAccountHistory(list) {
  if (!isValidHistory(list)) return;
  writeHistory(list);
}

export function getCachedHuntLocations() {
  if (memory.locations && isValidLocations(memory.locations.data)) {
    return memory.locations.data;
  }
  const stored = readStorageEntry(HUNT_LOCATIONS_KEY, isValidLocations);
  if (!stored) return null;
  memory.locations = stored;
  return stored.data;
}

export function setCachedHuntLocations(list) {
  if (!isValidLocations(list)) return;
  writeLocations(list);
}

export function getCachedNotifications(filter = 'all') {
  const key = notificationsListKey(filter);
  const mem = memory.notifications.get(key);
  if (mem && isValidNotificationsPayload(mem.data)) return mem.data;
  const stored = readStorageEntry(key, isValidNotificationsPayload);
  if (!stored) return null;
  memory.notifications.set(key, stored);
  return stored.data;
}

export function setCachedNotifications(filter, data) {
  const payload = normalizeNotifications(data);
  if (!payload) return;
  writeNotifications(filter, payload);
}

export function invalidateCachedAccountHistory() {
  memory.history = null;
  inFlightHistory = null;
  try {
    sessionStorage.removeItem(ACCOUNT_HISTORY_KEY);
  } catch {
    // ignore
  }
}

export function invalidateCachedHuntLocations() {
  memory.locations = null;
  inFlightLocations = null;
  try {
    sessionStorage.removeItem(HUNT_LOCATIONS_KEY);
  } catch {
    // ignore
  }
}

export function invalidateNotificationsCache() {
  memory.notifications.clear();
  inFlightNotifications.clear();
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(NOTIFICATIONS_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function invalidateAccountHubCache() {
  invalidateCachedAccountHistory();
  invalidateCachedHuntLocations();
  invalidateNotificationsCache();
}

export function fetchAccountHistory(client, { force = false } = {}) {
  if (!client || typeof client.get !== 'function') {
    return Promise.reject(new Error('api client required'));
  }
  if (!force) {
    const fresh = getCachedAccountHistory();
    if (fresh) return Promise.resolve({ data: { results: fresh } });
  }
  if (inFlightHistory) return inFlightHistory;

  inFlightHistory = client
    .get(ACCOUNT_HISTORY_PATH)
    .then((res) => {
      const results = normalizeHistory(res?.data) || [];
      setCachedAccountHistory(results);
      return { ...res, data: { ...(res?.data || {}), results } };
    })
    .finally(() => {
      inFlightHistory = null;
    });

  return inFlightHistory;
}

export function fetchHuntLocations(client, { force = false } = {}) {
  if (!client || typeof client.get !== 'function') {
    return Promise.reject(new Error('api client required'));
  }
  if (!force) {
    const fresh = getCachedHuntLocations();
    if (fresh) return Promise.resolve({ data: fresh });
  }
  if (inFlightLocations) return inFlightLocations;

  inFlightLocations = client
    .get(HUNT_LOCATIONS_PATH)
    .then((res) => {
      const list = normalizeLocations(res?.data) || [];
      setCachedHuntLocations(list);
      return { ...res, data: list };
    })
    .finally(() => {
      inFlightLocations = null;
    });

  return inFlightLocations;
}

export async function fetchNotificationsList(filter, loader, { force = false } = {}) {
  if (typeof loader !== 'function') {
    return Promise.reject(new Error('loader required'));
  }
  const key = filter || 'all';
  if (!force) {
    const fresh = getCachedNotifications(key);
    if (fresh) return fresh;
  }
  if (inFlightNotifications.has(key)) return inFlightNotifications.get(key);

  const pending = Promise.resolve(loader())
    .then((data) => {
      const payload = normalizeNotifications(data) || { results: [], count: 0 };
      setCachedNotifications(key, payload);
      return payload;
    })
    .finally(() => {
      inFlightNotifications.delete(key);
    });

  inFlightNotifications.set(key, pending);
  return pending;
}
